import { useCallback, useMemo, useState, useEffect } from 'react';
import AdSection from './media/AdSection';

const VIDEO_TYPES = {
  google: [
    { key: 'VIDEO', label: 'Vídeos' },
    { key: 'BUMPER', label: 'Vídeos Bumper' }
  ],
  meta: [
    { key: 'VIDEO', label: 'Vídeos' }
  ]
};

const VALIDATION_RULES = {
  VIDEO: { min: 1, max: 5 },
  BUMPER: { min: 0, max: 3 },
};

const WARNING_MESSAGES = {
  MIN_REQUIRED: (min, type) => `Debe haber al menos ${min} ${type.toLowerCase()}.`,
  MAX_EXCEEDED: (max, type) => `Debe haber como máximo ${max} ${type.toLowerCase()}.`,
  EXACT_ONE: (type) => `Debe haber exactamente 1 ${type.toLowerCase()}.`
};

export default function VideosSection({ data, editMode, onDataChange, platform }) {
  const types = useMemo(() => VIDEO_TYPES[platform] || [], [platform]);
  const [previews, setPreviews] = useState({});
  const [updates, setUpdates] = useState({});
  const [warnings, setWarnings] = useState({});
  const [expanded, setExpanded] = useState(() => {
    const init = {};
    if (data.ad_content?.length) init[data.ad_content[0].id] = true;
    return init;
  });

  useEffect(() => {
    setPreviews({});
    setUpdates({});
    setWarnings({});
  }, [data.ad_content]);

  const getInitialTypesForAd = useCallback(ad => {
    const urls = ad.video_urls || [];
    const existing = [...new Set(urls.map(v => v.type))];
    return types.filter(t => existing.includes(t.key));
  }, [types]);

  useEffect(() => {
    const newWarnings = {};
    data.ad_content.forEach(ad => {
      const adId = ad.id;
      const vids = ad.video_urls || [];
      const { to_add = [], to_remove = [] } = updates[adId]?.assets?.videos || {};
      const removedIds = to_remove.map(r => r.asset_group_resource_name || r.id);
      const adWarn = {};
      getInitialTypesForAd(ad).forEach(({ key: type, label }) => {
        const original = vids.filter(v => v.type === type && !removedIds.includes(v.asset_group_resource_name || v.id));
        const added = to_add.filter(a => a.type === type);
        const total = original.length + added.length;
        if (platform === 'meta') {
          if (total !== 1) adWarn[type] = WARNING_MESSAGES.EXACT_ONE(label);
        } else {
          const rules = VALIDATION_RULES[type];
          if (!rules) return;
          if (rules.min === rules.max && rules.min === 1) {
            if (total !== 1) adWarn[type] = WARNING_MESSAGES.EXACT_ONE(label);
          } else if (total < rules.min) {
            adWarn[type] = WARNING_MESSAGES.MIN_REQUIRED(rules.min, label);
          } else if (total > rules.max) {
            adWarn[type] = WARNING_MESSAGES.MAX_EXCEEDED(rules.max, label);
          }
        }
      });
      newWarnings[adId] = adWarn;
    });
    setWarnings(newWarnings);
  }, [data.ad_content, updates, types, platform, getInitialTypesForAd]);

  const notifyChange = (newUpdates, newPreviews) => {
    if (!editMode) return;
    const fileMap = Object.values(newPreviews)
      .flatMap(ad => Object.values(ad || {}))
      .flat()
      .reduce((map, p) => {
        map[p.file_placeholder] = p.file;
        return map;
      }, {});
    onDataChange?.({ assetsUpdates: newUpdates, fileMap });
  };

  const handleToggle = adId => setExpanded(prev => ({ ...prev, [adId]: !prev[adId] }));

  const handleUpload = (adId, type, files) => {
    if (platform === 'meta') {
      const ad = data.ad_content.find(a => a.id === adId);
      const originals = (ad.video_urls || []).filter(v => v.type === type);
      if (!originals.length) return;
      const removed = originals.map(v => ({ asset_group_resource_name: v.asset_group_resource_name || v.id }));
      const file = files[0];
      const placeholder = `file_${Date.now()}_${adId}_${type}`;
      const preview = { file, file_placeholder: placeholder, url: URL.createObjectURL(file), type };
      const newPreviews = { [adId]: { [type]: [preview] } };
      setPreviews(newPreviews);
      const newUpdates = {
        [adId]: {
          assets: {
            videos: { to_add: [{ type, file_placeholder: placeholder }], to_remove: removed }
          }
        }
      };
      setUpdates(newUpdates);
      notifyChange(newUpdates, newPreviews);
      return;
    }
    const list = Array.from(files).map((file, idx) => ({
      file,
      file_placeholder: `file_${Date.now()}_${adId}_${type}_${idx}`,
      url: URL.createObjectURL(file),
      type
    }));
    const newPreviews = {
      ...previews,
      [adId]: {
        ...(previews[adId] || {}),
        [type]: [...(previews[adId]?.[type] || []), ...list]
      }
    };
    setPreviews(newPreviews);
    const prev = updates[adId]?.assets?.videos || { to_add: [], to_remove: [] };
    const newUpdates = {
      ...updates,
      [adId]: {
        assets: {
          videos: {
            to_add: [...prev.to_add, ...list.map(i => ({ type: i.type, file_placeholder: i.file_placeholder }))],
            to_remove: prev.to_remove
          }
        }
      }
    };
    setUpdates(newUpdates);
    notifyChange(newUpdates, newPreviews);
  };

  const handleRemove = ({ adId, type, id, isNew }) => {
    let newPreviews = previews;
    let newUpdates = updates;
    if (isNew) {
      newPreviews = {
        ...previews,
        [adId]: {
          ...(previews[adId] || {}),
          [type]: (previews[adId]?.[type] || []).filter(p => p.file_placeholder !== id)
        }
      };
      setPreviews(newPreviews);
      const prev = updates[adId]?.assets?.videos || { to_add: [], to_remove: [] };
      newUpdates = {
        ...updates,
        [adId]: {
          assets: {
            videos: {
              to_add: prev.to_add.filter(u => u.file_placeholder !== id),
              to_remove: prev.to_remove
            }
          }
        }
      };
    } else {
      const prev = updates[adId]?.assets?.videos || { to_add: [], to_remove: [] };
      newUpdates = {
        ...updates,
        [adId]: {
          assets: {
            videos: {
              to_add: prev.to_add,
              to_remove: [...prev.to_remove, { asset_group_resource_name: id }]
            }
          }
        }
      };
    }
    setUpdates(newUpdates);
    notifyChange(newUpdates, newPreviews);
  };

  const adsWithVideos = data.ad_content.filter(ad => getInitialTypesForAd(ad).length > 0);

  return (
    <div className="space-y-6 mt-6">
      <h3 className="text-sm font-medium text-gray-500 mb-3">Vídeos por anuncio</h3>
      <div className="space-y-6">
        {adsWithVideos.map(ad => (
          <AdSection
            key={ad.id}
            ad={ad}
            types={getInitialTypesForAd(ad)}
            updates={updates}
            previews={previews}
            warnings={warnings}
            expanded={expanded[ad.id] || false}
            onToggle={handleToggle}
            onUpload={handleUpload}
            onRemove={handleRemove}
            editMode={platform === 'meta' && editMode && (ad.video_urls || []).length > 0}
            mediaKey="video_urls"
            assetKey="videos"
            acceptedFiles="video/*"
            uploadDescription="Formatos aceptados: MP4, MOV"
          />
        ))}
      </div>
    </div>
  );
}
